import {stringContainsUrl} from '@edume/bento/urlUtil';
import {EMOJIES, sanitizeHTMLForTextEditor} from '@edume/magnificent';

import {isKeyExcludedFromDuplication} from '../SlideshowCanvas/stateUtils';

type DeepPartialAndNullable<T> = {
  [P in keyof T]?: T[P] extends object
    ? DeepPartialAndNullable<T[P]> | null
    : T[P] | null;
};

/**
 * Recursively removes or provides falsy values to fields that should not be duplicated or edited by the assistant.
 * This includes fields that contain URLs, images, videos or cKey and id fields.
 */
export const removeNonEditableFields = <T extends object>(
  obj: T
): DeepPartialAndNullable<T> => {
  if (Array.isArray(obj)) {
    return obj.map((o) =>
      removeNonEditableFields(o)
    ) as unknown as DeepPartialAndNullable<T>;
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([key, value]): [string, unknown] | [] => {
          if (typeof value === 'string' && stringContainsUrl(value)) {
            return [key, ''];
          }

          if (key.endsWith('Ckey') && !value) {
            return [key, value];
          }

          if (isKeyExcludedFromDuplication(key)) {
            return [];
          }
          if (key.toLowerCase() === 'image') {
            return [key, null];
          }

          if (key.toLowerCase().includes('url') && typeof value === 'string') {
            return [key, ''];
          }

          if (key === 'description' && typeof value === 'string') {
            return [key, sanitizeHTMLForTextEditor(value)];
          }

          if (typeof value !== 'object') {
            return [key, value];
          }

          return [key, removeNonEditableFields(value)];
        })
        .filter(([key]) => key !== undefined)
    ) as DeepPartialAndNullable<T>;
  }
  return obj;
};

const VALID_EMOJI_CODES = new Set(
  Object.values(EMOJIES).flatMap((category) =>
    Array.isArray(category)
      ? category.map((emoji) => emoji.unified.toLowerCase())
      : []
  )
);

// eslint-disable-next-line complexity
const validateEmojiCodesInAssistantPatch = <T>(
  obj: T
): DeepPartialAndNullable<T> => {
  const toUnicodeHexSequence = (s: string) =>
    Array.from(s)
      .map((ch) => ch.codePointAt(0)?.toString(16))
      .filter((x): x is string => !!x)
      .join('-')
      .toLowerCase();

  const normalizeEmojiCode = (input: string) => {
    const trimmed = input.trim();
    const hexSeqRe = /^[0-9a-f]+(?:-[0-9a-f]+)*$/i;
    return hexSeqRe.test(trimmed)
      ? trimmed.toLowerCase()
      : toUnicodeHexSequence(trimmed);
  };

  const FALLBACK_EMOJI_CODE = '1f937-200d-2642-fe0f'; // 🤷

  const getPathSegments = (path: string) =>
    path
      .split('/')
      .slice(1)
      .map((s) => s.replace(/~1/g, '/').replace(/~0/g, '~'));

  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // This handles replacing emojis in the assistant patch
  // otherwise we continue below for adding emojis
  if ('path' in obj && typeof obj.path === 'string') {
    const pathSegments = getPathSegments(obj.path);
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (lastSegment === 'code') {
      if ('value' in obj && typeof obj.value === 'string') {
        const normalized = normalizeEmojiCode(obj.value);

        return {
          ...obj,
          value: VALID_EMOJI_CODES.has(normalized)
            ? normalized
            : FALLBACK_EMOJI_CODE,
        };
      }
    }
  }

  if (Array.isArray(obj)) {
    return obj.map((o) =>
      validateEmojiCodesInAssistantPatch(o)
    ) as unknown as T;
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]): [string, unknown] => {
      if (key.toLowerCase() === 'code' && typeof value === 'string') {
        const normalized = normalizeEmojiCode(value);
        return [
          key,
          VALID_EMOJI_CODES.has(normalized) ? normalized : FALLBACK_EMOJI_CODE,
        ];
      }

      if (typeof value !== 'object' || value === null) {
        return [key, value];
      }

      return [key, validateEmojiCodesInAssistantPatch(value)];
    })
  ) as unknown as T;
};

export const sanitizeAssistantContent = <T extends object>(
  obj: T
): DeepPartialAndNullable<T> => {
  if ('value' in obj) {
    const sanitizedValue = removeNonEditableFields({value: obj.value}).value;
    return validateEmojiCodesInAssistantPatch({
      ...obj,
      value: sanitizedValue,
    }) as unknown as T;
  }

  return validateEmojiCodesInAssistantPatch(
    removeNonEditableFields(obj)
  ) as unknown as T;
};

export const validateAssistantMessage = (message: string) => {
  if (stringContainsUrl(message)) {
    return {
      isValid: false,
      error: 'URL_FOUND',
      details: 'URLs are not allowed in assistant messages',
    };
  }

  return {
    isValid: true,
    error: null,
    details: null,
  };
};
